import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="confirm"
// A reusable confirmation dialog that intercepts actions and shows a modal
//
// Usage with link/button:
//   <button data-controller="confirm"
//           data-confirm-title-value="Remove Truck?"
//           data-confirm-message-value="Are you sure you want to remove this truck?"
//           data-confirm-details-value="Stock #10026 - 2020 Chevy 1500"
//           data-confirm-confirm-text-value="Remove Truck"
//           data-confirm-destructive-value="true"
//           data-action="click->confirm#show"
//           data-turbo-method="delete"
//           href="/purchases/1/trucks/2">
//     Remove
//   </button>
//
// Or use with a form:
//   <form data-controller="confirm"
//         data-confirm-title-value="Delete Purchase?"
//         data-confirm-message-value="This action cannot be undone."
//         data-action="submit->confirm#intercept">
//     ...
//   </form>
//
// The dialog will be created dynamically and removed after use.
export default class extends Controller {
  static values = {
    title: { type: String, default: "Confirm" },
    message: { type: String, default: "Are you sure?" },
    details: { type: String, default: "" },
    note: { type: String, default: "" },
    confirmText: { type: String, default: "Confirm" },
    cancelText: { type: String, default: "Cancel" },
    destructive: { type: Boolean, default: false }
  }

  connect() {
    this.dialog = null
    this.pendingAction = null
  }

  disconnect() {
    this.removeDialog()
  }

  show(event) {
    event.preventDefault()
    event.stopPropagation()

    // Store the original element and action
    this.pendingAction = {
      element: event.currentTarget,
      event: event,
      href: event.currentTarget.href,
      method: event.currentTarget.dataset.turboMethod || "get"
    }

    this.createDialog()
    this.dialog.showModal()
  }

  intercept(event) {
    // If we're in a confirmed state, let it through
    if (this.confirmed) {
      this.confirmed = false
      return
    }

    event.preventDefault()
    event.stopPropagation()

    this.pendingAction = {
      element: event.target,
      event: event,
      isForm: true
    }

    this.createDialog()
    this.dialog.showModal()
  }

  confirm(event) {
    event.preventDefault()
    this.removeDialog()

    if (this.pendingAction) {
      if (this.pendingAction.isForm) {
        // For forms, set confirmed flag and resubmit
        this.confirmed = true
        this.pendingAction.element.requestSubmit()
      } else if (this.pendingAction.href) {
        // For links with turbo-method
        const link = document.createElement("a")
        link.href = this.pendingAction.href
        link.dataset.turboMethod = this.pendingAction.method
        link.style.display = "none"
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    }

    this.pendingAction = null
  }

  cancel(event) {
    event?.preventDefault()
    this.removeDialog()
    this.pendingAction = null
  }

  createDialog() {
    this.removeDialog()

    const buttonClass = this.destructiveValue ? "btn btn-error" : "btn btn-primary"

    const dialogHtml = `
      <dialog class="modal">
        <div class="modal-box">
          <h3 class="text-lg font-medium">${this.escapeHtml(this.titleValue)}</h3>

          <div class="py-4">
            <p class="text-base-content/80">${this.escapeHtml(this.messageValue)}</p>

            ${this.detailsValue ? `
              <div class="mt-4 p-3 bg-base-200 rounded-lg">
                <p class="font-medium">${this.escapeHtml(this.detailsValue)}</p>
              </div>
            ` : ""}

            ${this.noteValue ? `
              <p class="mt-4 text-sm text-secondary">${this.escapeHtml(this.noteValue)}</p>
            ` : ""}
          </div>

          <div class="modal-action">
            <button class="btn btn-ghost" data-role="cancel">
              ${this.escapeHtml(this.cancelTextValue)}
            </button>
            <button class="${buttonClass}" data-role="confirm">
              ${this.escapeHtml(this.confirmTextValue)}
            </button>
          </div>
        </div>

        <form method="dialog" class="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    `

    const template = document.createElement("template")
    template.innerHTML = dialogHtml.trim()
    this.dialog = template.content.firstChild

    // Bind event handlers directly since dialog is outside controller scope
    this.dialog.querySelector('[data-role="cancel"]').addEventListener("click", (e) => this.cancel(e))
    this.dialog.querySelector('[data-role="confirm"]').addEventListener("click", (e) => this.confirm(e))
    this.dialog.addEventListener("close", () => this.cancel())

    document.body.appendChild(this.dialog)
  }

  removeDialog() {
    if (this.dialog) {
      this.dialog.close()
      this.dialog.remove()
      this.dialog = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
